import { useEffect, useRef, useState } from "react";

import type { PartySocketOptions } from "partysocket";
import usePartySocket from "partysocket/react";

/**
 * A wrapper around usePartySocket that adds:
 * 1. Enhanced logging for debugging connection issues
 * 2. Detection of stuck reconnection loops with recovery
 * 3. Preserves PartySocket's built-in reconnection mechanism
 * 4. Special handling for Cloudflare resource errors
 */
export function useStablePartySocket(
  config: PartySocketOptions & {
    onOpen?: (event: WebSocketEventMap["open"]) => void;
    onClose?: (event: WebSocketEventMap["close"]) => void;
    onError?: (event: WebSocketEventMap["error"]) => void;
    onMessage?: (event: WebSocketEventMap["message"]) => void;
  },
) {
  // This key will force recreation of the socket when changed
  const [socketKey, setSocketKey] = useState("initial");

  // Track connection state for detecting stuck reconnection loops
  const connectionRef = useRef({
    // Time when we first detected a potential reconnection issue
    problemDetectedAt: 0,
    // Time of last successful connection
    lastConnectedAt: 0,
    // Time of most recent close event
    lastCloseAt: 0,
    // Number of close events since last successful connection
    closeCount: 0,
    // Timeout for resetting connection
    resetTimeout: null as ReturnType<typeof setTimeout> | null,
    // Whether we're currently in a potential reconnection loop
    potentialLoopDetected: false,
    // Whether we've seen a resource error
    resourceErrorDetected: false,
    // Last time we attempted to reconnect after a resource error
    lastResourceErrorReconnectAt: 0,
  });

  // Enhance the config with our logging and detection logic
  const enhancedConfig = {
    ...config,
    key: socketKey, // This is crucial - changing this recreates the socket

    onOpen: (event: WebSocketEventMap["open"]) => {
      const timestamp = new Date().toISOString();
      const isReconnection = connectionRef.current.lastConnectedAt > 0;
      console.log(
        `[${timestamp}] PartySocket ${isReconnection ? "re" : ""}connected`,
        {
          room: config.room,
          url: (event.target as WebSocket).url,
          isReconnection,
        },
      );

      // If this is a reconnection, dispatch a special reconnection event
      if (isReconnection && config.room?.includes("feedback")) {
        // Only send for feedback party connections
        try {
          // We're dispatching a custom event that can be listened for
          // This allows components to respond to reconnection events
          window.dispatchEvent(
            new CustomEvent("partysocket:reconnected", {
              detail: {
                room: config.room,
                party: config.room.split(":")[0],
                timestamp: Date.now(),
              },
            }),
          );

          console.log(
            `[${timestamp}] Dispatched reconnection event for room: ${config.room}`,
          );
        } catch (error) {
          console.error("Error dispatching reconnection event:", error);
        }
      }

      // Reset our connection tracking on successful connection
      connectionRef.current.lastConnectedAt = Date.now();
      connectionRef.current.closeCount = 0;
      connectionRef.current.potentialLoopDetected = false;
      connectionRef.current.resourceErrorDetected = false;

      // Call the original onOpen if provided
      if (config.onOpen) {
        config.onOpen(event);
      }
    },

    onClose: (event: WebSocketEventMap["close"]): void => {
      const now = Date.now();
      const timestamp = new Date().toISOString();

      // Check for Cloudflare resource errors
      const isResourceError =
        typeof event.reason === "string" &&
        event.reason.includes("Insufficient resources");

      // Log detailed close information
      console.log(`[${timestamp}] PartySocket closed`, {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        room: config.room ?? "undefined",
        timeSinceLastConnect: `${
          now - connectionRef.current.lastConnectedAt
        }ms`,
        closeCount: connectionRef.current.closeCount + 1,
        isResourceError,
      });

      // Update our connection tracking
      connectionRef.current.lastCloseAt = now;
      connectionRef.current.closeCount++;

      // Special handling for resource errors
      if (isResourceError) {
        connectionRef.current.resourceErrorDetected = true;

        // Calculate how long to wait before reconnecting
        // For resource errors, use a much more aggressive backoff
        const minBackoffTime = 5000; // Start with at least 5 seconds
        const backoffTime = Math.max(
          minBackoffTime,
          Math.min(60000, 5000 * 2 ** connectionRef.current.closeCount), // Cap at 1 minute
        );

        console.log(
          `[${timestamp}] Resource error detected, delaying reconnection for ${backoffTime}ms`,
        );

        // Cancel any existing reconnection attempts from PartySocket
        // We'll handle reconnection ourselves for resource errors
        if (event.target) {
          // This is a hack to prevent PartySocket from reconnecting immediately
          // @ts-expect-error - accessing private property
          if ((event.target as WebSocket)._retryCount !== undefined) {
            // @ts-expect-error - accessing private property
            (event.target as WebSocket)._retryCount = 999; // Set to a high number to prevent reconnection
          }
        }

        // Schedule our own reconnection with longer delay
        if (connectionRef.current.resetTimeout) {
          clearTimeout(connectionRef.current.resetTimeout);
        }

        connectionRef.current.resetTimeout = setTimeout(() => {
          console.log(
            `[${new Date().toISOString()}] Attempting reconnection after resource error`,
          );
          connectionRef.current.lastResourceErrorReconnectAt = Date.now();
          setSocketKey(`resource-error-reset-${Date.now()}`);
          connectionRef.current.resetTimeout = null;
        }, backoffTime);

        // Call the original onClose if provided
        if (config.onClose) {
          config.onClose(event);
        }

        return; // Skip the normal reconnection logic
      }

      // Normal reconnection loop detection (for non-resource errors)
      const timeSinceLastConnect = now - connectionRef.current.lastConnectedAt;

      // If we've had multiple close events and it's been a while since a successful connection
      if (
        connectionRef.current.closeCount > 3 &&
        timeSinceLastConnect > 10000
      ) {
        // If we haven't already detected a potential loop
        if (!connectionRef.current.potentialLoopDetected) {
          connectionRef.current.potentialLoopDetected = true;
          connectionRef.current.problemDetectedAt = now;
          console.log(
            `[${timestamp}] Potential reconnection issue detected, monitoring...`,
          );
        } else {
          // We've already detected a potential loop, check if it's been going on for too long
          const loopDuration = now - connectionRef.current.problemDetectedAt;

          // If the reconnection issue has persisted for more than 30 seconds,
          // and we don't already have a reset scheduled
          if (loopDuration > 30000 && !connectionRef.current.resetTimeout) {
            console.log(
              `[${timestamp}] Reconnection issue persisted for ${loopDuration}ms, will reset connection`,
            );

            // Schedule a reset
            connectionRef.current.resetTimeout = setTimeout(() => {
              console.log(
                `[${new Date().toISOString()}] Resetting PartySocket connection to break potential loop`,
              );
              setSocketKey(`reset-${Date.now()}`);
              connectionRef.current.resetTimeout = null;
              connectionRef.current.potentialLoopDetected = false;
              connectionRef.current.closeCount = 0;
            }, 1000);
          }
        }
      }

      // Call the original onClose if provided
      if (config.onClose) {
        config.onClose(event);
      }
    },

    onError: (event: WebSocketEventMap["error"]) => {
      const timestamp = new Date().toISOString();
      console.error(`[${timestamp}] PartySocket error`, {
        room: config.room || "undefined",
        error: event,
      });

      // Call the original onError if provided
      if (config.onError) {
        config.onError(event);
      }
    },

    onMessage: (event: WebSocketEventMap["message"]) => {
      // We could log all messages, but that might be too verbose
      // Instead, let's look for specific messages that might help diagnose issues
      try {
        const data = JSON.parse(event.data as string);

        // If the message has a type field, it might be a control message
        if (data.type === "error" || data.type === "system") {
          const timestamp = new Date().toISOString();
          console.log(`[${timestamp}] PartySocket system message:`, data);
        }
      } catch {
        // Not JSON or other parsing error, ignore
      }

      // Call the original onMessage if provided
      if (config.onMessage) {
        config.onMessage(event);
      }
    },
  };

  // Use the enhanced config with the original hook
  const socket = usePartySocket(enhancedConfig);

  // Clean up timeouts
  useEffect(() => {
    // Capture the current value of the timeout ID
    const resetTimeout = connectionRef.current.resetTimeout;

    return () => {
      if (resetTimeout) {
        clearTimeout(resetTimeout);
      }
    };
  }, []);

  // Add component lifecycle logging
  useEffect(() => {
    console.log(
      `[${new Date().toISOString()}] Component using PartySocket mounted`,
      {
        room: config.room || "undefined",
      },
    );

    return () => {
      console.log(
        `[${new Date().toISOString()}] Component using PartySocket unmounted`,
        {
          room: config.room || "undefined",
        },
      );
    };
  }, [config.room]);

  return socket;
}
